import { cls } from "cls";
import React from "react";
import { connect } from "react-redux";
import { RootState, UserState } from "~/common/Store/models/RootReadonly";
import Chevron from "./icons/Chevron";
import RewardsBadge from "./Logos/RewardsBadge";
import { PageCreatorBanner } from "./PageCreatorBanner";
import PlusPromotion from "./PlusPromotion";
import RewardsText from "./RewardsBannerText";
import SiteWideEventDayBanner from "./SiteWideEventDayBanner";

interface AlertBannerRightContainerProps {
  user: UserState;
  isCustomerServiceMessageAvailable: boolean;
  eventDayBannerVisible: boolean;
  mounted: boolean;
  isEventDayDataLoading: boolean;
  isPlusEssentials?: boolean;
  pageCreatorBanner: PageCreatorBanner | null;
}

const AlertBannerRightContainer = ({
  user,
  isCustomerServiceMessageAvailable,
  eventDayBannerVisible,
  isEventDayDataLoading,
  isPlusEssentials,
  pageCreatorBanner,
}: AlertBannerRightContainerProps) => {
  const { textColor } = pageCreatorBanner ?? { textColor: "" };

  const changeStylesDependingOnEvent = (
    plusEssentialColor: string,
    alertBannerDefaultColor: string,
  ) => {
    if (textColor && textColor.length > 3 && !isPlusEssentials) {
      return textColor;
    }
    if (isPlusEssentials) {
      return plusEssentialColor;
    }
    return alertBannerDefaultColor;
  };

  let shortTextClassNames = "block sm:hidden";
  let longTextClassNames = "hidden sm:block";

  if (eventDayBannerVisible) {
    shortTextClassNames = "block";
    longTextClassNames = "hidden";
  }

  if (isCustomerServiceMessageAvailable) {
    shortTextClassNames = "sm:hidden lt:block lz:hidden";
    longTextClassNames = "hidden sm:block lt:hidden lz:block";
  }

  if (eventDayBannerVisible && isCustomerServiceMessageAvailable) {
    shortTextClassNames = "sm:hidden lt:block";
    longTextClassNames = "hidden sm:block lt:hidden";
  }

  return (
    <div
      data-testid="alert-banner-right-container"
      className="space-x-6 items-center justify-center lt:justify-between flex"
    >
      {isCustomerServiceMessageAvailable &&
        !isPlusEssentials &&
        pageCreatorBanner && (
          <SiteWideEventDayBanner
            pageCreatorBanner={pageCreatorBanner}
            classNames="hidden xl:flex"
          />
        )}
      {!isEventDayDataLoading ? (
        <div
          className={cls(
            !eventDayBannerVisible && // classes to be applied when there are no special properties
              !isPlusEssentials &&
              !isEventDayDataLoading
              ? "flex"
              : "",
            (eventDayBannerVisible && !isEventDayDataLoading) ||
              (isPlusEssentials && !isEventDayDataLoading) // classes to be applied when there is a special property
              ? "hidden lt:flex"
              : "",
            "space-x-6",
          )}
        >
          {!user.hasWebPlus && !isPlusEssentials && (
            <PlusPromotion
              user={user}
              textColorOverride={textColor}
              shortTextClassNames={shortTextClassNames}
              longTextClassNames={longTextClassNames}
              chevronStrokeColor={changeStylesDependingOnEvent(
                "#FFFFFF",
                "#3B4238",
              )}
            />
          )}
          <a
            aria-label="Rewards"
            href={
              user.isRewardsMember ? "/myaccount/payment/#credits" : "/rewards/"
            }
            className={cls(
              "inline-flex",
              "items-center",
              "font-semibold",
              "text-xs",
              isPlusEssentials ? "text-white" : "text-gray-800",
              "hover:underline",
              "no-underline",
            )}
            style={textColor && !isPlusEssentials ? { color: textColor } : {}}
            data-testid="rewards-ad"
          >
            <RewardsBadge
              classNames={cls(
                isPlusEssentials || textColor ? "" : "mix-blend-multiply",
                "pr-2",
              )}
            />
            <RewardsText
              user={user}
              rewardsTextClassNames={
                eventDayBannerVisible ? "hidden lt:block" : "block"
              }
              shortTextClassNames={shortTextClassNames}
              longTextClassNames={longTextClassNames}
            />
            <Chevron
              strokeColor={changeStylesDependingOnEvent("#FFFFFF", "#3b4238")}
              classNames="pl-2"
            />
          </a>
        </div>
      ) : null}
    </div>
  );
};

export const mapStateToProps = (
  state: RootState,
  ownProps: AlertBannerRightContainerProps,
) => {
  if (!ownProps.mounted) {
    return {};
  }

  return state;
};

export default connect(mapStateToProps)(AlertBannerRightContainer);
